/* eslint-disable react/prop-types */
// @ts-check
import React, { useState, useEffect } from 'react';
import Draggable from 'react-draggable';
import clsx from 'classnames';

import PropTypes from 'prop-types';

import {
  Button,
  Card,
  CardImg,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';

import { RejectForm } from '../../components/forms/RejectForm';
import { SoftRejectForm } from '../../components/forms/SoftRejectForm';
import './SubmissionComponents.scss';
import './Submission.scss';

const STORAGES = {
  AWS_S3: 'AWS_S3',
  GOOGLE_DRIVE: 'GOOGLE_DRIVE',
};

const getImageUrl = (
  { storage = STORAGES.GOOGLE_DRIVE, url, preSignedUrl = '' },
  full_size,
) => {
  switch (storage) {
    case STORAGES.AWS_S3:
      return preSignedUrl;
    case STORAGES.GOOGLE_DRIVE: {
      if (full_size) return url;

      const imageId = url.split('id=')[1];
      return `https://drive.google.com/thumbnail?id=${imageId}&sz=w430`;
    }
    default:
      return '';
  }
};

const renderImages = (data, setter) => {
  const images = [];
  data.forEach((img, index) => {
    const url = getImageUrl(img);
    img.thumb = url;
    images.push(<ImageListItem key={index} img={img} onClick={setter} />);
  });
  return images;
};

export const ImagesComponent = ({ currentImage, images, imageSetter }) => {
  if (images.length > 0) {
    return (
      <div className='images-component'>
        <ImageCard image={currentImage} fullSize rotateEnabled scaleEnabled />
        <ul className='imageList'>{renderImages(images, imageSetter)}</ul>
      </div>
    );
  }
  return (
    <div>
      <p>no images to show</p>
      <h2>:-(</h2>
    </div>
  );
};

export const ImageInfo = ({ image }) => (
  <div target='infoButton'>
    <table className='info-container'>
      {image &&
        image.evidence.map((ev) => (
          <tbody key={ev.testName}>
            <tr>
              <td>{`${ev.testName}:  `}</td>
              <td>{ev.score === 1 ? 'pass' : 'fail'}</td>
            </tr>
            <tr>
              <td colSpan='2'>{ev.output}</td>
            </tr>
          </tbody>
        ))}
    </table>
  </div>
);

export const StoreMarker = () => (
  <div
    style={{
      height: '55px',
      width: '55px',
      color: 'white',
      background: '#553322',
      /* padding: 7px 36px; */
      display: 'inline-flex',
      textAlign: 'center',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '49%',
      transform: 'translate(-50%, -50%)',
    }}>
    Store
  </div>
);

export const RejectionModal = ({ isOpen, toggle, handleSubmit, data }) => (
  <Modal isOpen={isOpen} toggle={toggle} className='rejectionModal' size='lg'>
    <ModalHeader toggle={toggle}>Rejection! Doh!</ModalHeader>
    <ModalBody className='rejectionBody'>
      <div>
        <RejectForm
          handleSubmit={handleSubmit}
          closeForm={toggle}
          optionsData={{ data, key: 'id', value: 'reasonString' }}
        />
      </div>
    </ModalBody>
    <ModalFooter />
  </Modal>
);

export const SoftRejectionModal = ({ isOpen, toggle, handleSubmit, data }) => (
  <Modal isOpen={isOpen} toggle={toggle} className='' size='lg'>
    <ModalHeader toggle={toggle}>Soft Rejection! Doh!</ModalHeader>
    <ModalBody className='rejectionBody'>
      <div>
        <SoftRejectForm
          handleSubmit={handleSubmit}
          closeForm={toggle}
          optionsData={{ data, key: 'id', value: 'reasonString' }}
        />
      </div>
    </ModalBody>
    <ModalFooter />
  </Modal>
);

export const ImageMarker = ({ url }) => (
  <div>
    <img src={url} className='imageMarker' alt='marker' />
  </div>
);

export const ImageListItem = ({ img, onClick }) => (
  <li className={`imageListItem score${img.score}`}>
    <Button
      className={`thumbButton score${img.score}`}
      onClick={() => onClick(img)}>
      <img className='thumb' src={img.thumb} alt={img.text} />
    </Button>
  </li>
);

export const QuestionRow = ({ q, a }) => (
  <tr className='embeded-row'>
    <td className='question'>{q}</td>
    <td className='answer'>{`${a}`}</td>
  </tr>
);

export const SkuHeader = ({ sku }) => (
  <tr className='questionRow'>
    {Object.keys(sku).map((key, index) => (
      <th key={index} className='answer'>
        {key}
      </th>
    ))}
  </tr>
);

export const Table = ({ rows }) => (
  <table width='100%'>
    <tbody>{rows}</tbody>
  </table>
);

/**
 * @param {object} props
 * @param {Record<string, string>} props.sku
 * @param {number|string} props.index
 * @param {Array<string>|string} props.folio
 * @param {Array<string>|string} props.lookerID
 * @param {number} props.brandId
 */
export const SKURow = ({ sku, index, folio, lookerID, brandId }) => (
  <tr key={index} className='questionRow'>
    {Object.entries(sku).map((entry, skuIndex) => {
      const [name, val] = entry;
      const key = `${name}-${skuIndex}`;

      if (lookerID) {
        const validArr = Array.isArray(lookerID) && Array.isArray(folio);
        const validNamestring =
          lookerID.includes('VENTA 1') ||
          lookerID.includes('ICCID') ||
          lookerID.includes('ID');
        if (Array.isArray(folio)) {
          folio = folio.filter((item) => item); // clean empty values in array
        }
        const validFolio = folio.includes(val);
        const validData = validArr && validNamestring && validFolio;

        if (
          ((lookerID === 'VENTA 1' ||
            lookerID === 'ICCID' ||
            lookerID === 'ID') &&
            val === folio) ||
          validData
        ) {
          const folioClean = getFolio(folio, val);
          const status = getStatus(brandId, folioClean);

          return (
            <SKUCell status={status} key={key}>
              {val}
            </SKUCell>
          );
        }
        if (lookerID === 'CÓDIGO' && val === folio) {
          const folioClean = getFolio(folio, val);
          const status = getStatus(brandId, folioClean);

          return (
            <SKUCell status={status} key={key}>
              {val}
            </SKUCell>
          );
        }
      }

      return <SKUCell key={key}>{val}</SKUCell>;
    })}
  </tr>
);

/**
 * @param {Array<string>|string} folio
 * @param {string} val
 */
function getFolio(folio, val) {
  if (Array.isArray(folio) && val) {
    const matchedFolio = folio.find((item) => item === val);
    return matchedFolio ? matchedFolio.trim() : '';
  }
  if (!Array.isArray(folio)) {
    return folio ? folio.trim() : '';
  }

  return '';
}

/**
 * @param {number} brandId
 * @param {string} folio
 * @returns {'success'|'error'}
 */
function getStatus(brandId, folio) {
  if (
    brandId === 106 &&
    folio.length === 15 &&
    ['01', '02', '03'].some((item) => folio.endsWith(item))
  ) {
    return 'success';
  }

  if (
    [
      836, //  Metro Picking PE
      838, // Wong Picking PE
      840, // Pre-Picking Metro PE
      841, // Pre-Picking Wong
    ].includes(brandId)
  ) {
    return folio.length === 7 ? 'success' : 'error';
  }

  if (
    folio.length === 20 &&
    folio.startsWith('895214006') &&
    folio.toLowerCase().endsWith('f')
  ) {
    return 'success';
  }

  return 'error';
}

/**
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {'error' | 'success'} [props.status=false]
 */
function SKUCell(props) {
  const { status, children } = props;

  return (
    <td
      className={clsx('answer', {
        folioError: status === 'error',
        folioSuccess: status === 'success',
      })}>
      {children}
    </td>
  );
}

export const ImageCard = ({ image, rotateEnabled, scaleEnabled }) => {
  const zoomStep = 1;
  const maxScale = 5;
  const minScale = 1;
  const defaultScale = minScale;
  const defaultRotate = 0;

  const [barraButtonsVisibled, setBarraButtonsVisibled] = useState('');
  const [btnsZoomVisibled, setBtnsZoomVisibled] = useState('');
  const [btnsRotateVisibled, setBtnsRotateVisibled] = useState('');
  const [scale, setScale] = useState(defaultScale);
  const [rotate, setRotate] = useState(defaultRotate);
  const [version, setVersion] = useState(0);
  const isDraggable = scale > 0;

  useEffect(() => {
    if (!rotateEnabled && !scaleEnabled) {
      setBarraButtonsVisibled('none');
    }
    if (!rotateEnabled) {
      setBtnsRotateVisibled('none');
    }
    if (!scaleEnabled) {
      setBtnsZoomVisibled('none');
    }
  });

  const reset = () => {
    setScale(defaultScale);
    setRotate(0);
    setVersion(version + 1);
  };

  const zoomIn = () => {
    const newScale = scale + zoomStep;
    setScale(newScale <= maxScale ? newScale : maxScale);
  };

  const zoomOut = () => {
    const newScale = scale - zoomStep;
    setScale(newScale >= minScale ? newScale : minScale);
    if (scale === minScale) {
      setVersion(version + 1);
    }
  };

  const rotateImageRight = () => {
    setRotate(rotate === 270 ? 0 : rotate + 90);
  };

  const rotateImageLeft = () => {
    setRotate(rotate === 270 ? 0 : rotate - 90);
  };

  return (
    <React.Fragment>
      <Card className='imageCard'>
        <div className={`imagesBox score${image.score}`}>
          <Draggable disabled={!isDraggable} key={version}>
            <div
              className='containerDragg'
              style={isDraggable ? { cursor: 'move' } : undefined}>
              <CardImg
                style={{ transform: `scale(${scale}) rotate(${rotate}deg)` }}
                draggable='false'
                src={image.thumb}
              />
            </div>
          </Draggable>
        </div>
      </Card>
      <div
        className='buttonGroupContainer'
        style={{ display: `${barraButtonsVisibled}` }}>
        <Button onClick={reset}>Reset</Button>
        <div
          className='containerGroupButtonScale'
          style={{ display: `${btnsZoomVisibled}` }}>
          <Button onClick={zoomIn}>+</Button>
          <Button onClick={zoomOut}> - </Button>
        </div>
        <div
          className='containerGroupButtonRotate'
          style={{ display: `${btnsRotateVisibled}` }}>
          <Button onClick={rotateImageLeft} type='button'>
            <svg
              fill='currentColor'
              className='arrowRotate'
              viewBox='0 0 16 16'>
              <path
                fillRule='evenodd'
                d='M8 3a5 5 0 1 1-4.546 2.914.5.5 0 0 0-.908-.417A6 6 0 1 0 8 2v1z'
              />
              <path d='M8 4.466V.534a.25.25 0 0 0-.41-.192L5.23 2.308a.25.25 0 0 0 0 .384l2.36 1.966A.25.25 0 0 0 8 4.466z' />
            </svg>
          </Button>
          <Button onClick={rotateImageRight} type='button'>
            <svg
              fill='currentColor'
              className='arrowRotate'
              viewBox='0 0 16 16'>
              <path
                fillRule='evenodd'
                d='M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z'
              />
              <path d='M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z' />
            </svg>
          </Button>
        </div>
      </div>
    </React.Fragment>
  );
};

ImageMarker.propTypes = {
  url: PropTypes.string.isRequired,
};

ImageListItem.propTypes = {
  img: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
};

QuestionRow.propTypes = {
  a: PropTypes.string,
  q: PropTypes.any,
};

ImageCard.propTypes = {
  image: PropTypes.object.isRequired,
  rotateEnabled: PropTypes.bool.isRequired,
  scaleEnabled: PropTypes.bool.isRequired,
};

RejectionModal.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
};

SoftRejectionModal.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
};

ImageInfo.propTypes = {
  image: PropTypes.object.isRequired,
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
};

ImagesComponent.propTypes = {
  currentImage: PropTypes.object,
  imageSetter: PropTypes.func.isRequired,
  images: PropTypes.array.isRequired,
  infoState: PropTypes.bool.isRequired,
  toggleInfo: PropTypes.func.isRequired,
  toggleMap: PropTypes.func.isRequired,
};
